import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject,  interval,  Observable, Subject, Subscription,  throwError, timer } from 'rxjs';
import { SessionIdleConfigModel } from '../models/login.model';
import { ConfigService } from './config.service';
import { catchError, map, takeUntil } from 'rxjs/operators';


@Injectable({
  providedIn: 'root',
})
export class SessionIdleService implements OnDestroy {
  public idle_ttl_minutes = 15; // 15 mins
  private warning_ttl_seconds = 120;       // 2 mins before timeout


  // ⏱ Derived values
  private warningTime = this.warning_ttl_seconds * 1000;
  private warningCountdown = this.warning_ttl_seconds;


  private activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];
  private broadcast: BroadcastChannel;

  public session_warning$ = new BehaviorSubject<boolean>(false);

  public count_down$ = new BehaviorSubject<number>(this.warningCountdown);

  private session_logout_subject = new Subject<void>();
  public session_logout$ = this.session_logout_subject.asObservable();

  private reset$ = new Subject<void>();

  private warningTimeoutSub?: Subscription;
  private sessionTimeoutSub?: Subscription;
  private countdownSub?: Subscription;
  public session_expired = false;
  public show_warn_modal = false;
  private active = false; // watcher active?
  private destroy$ = new Subject<void>();
  constructor(
    private ngZone: NgZone,
    private api: HttpClient,
    private config: ConfigService,
  ) {

  }

    public GetSessionIdleSettings(): Observable<SessionIdleConfigModel> {
    return this.api.get<SessionIdleConfigModel>(this.config.getApiRoute('api/v3/client-portal/user-settings/config')).pipe(
      map(data => data),
      catchError((err: HttpErrorResponse) => throwError(err.error?.message ?? err.message))
    );
  }

  public initialize_config(config: SessionIdleConfigModel) {

    this.idle_ttl_minutes = config.idle_ttl_minutes;
    this.warning_ttl_seconds = config.warning_ttl_seconds;
    this.warningTime = this.warning_ttl_seconds * 1000;
    this.warningCountdown = this.warning_ttl_seconds;
     this.broadcast = new BroadcastChannel('session_channel');
    this.broadcast.onmessage = this.handleBroadcast.bind(this);

    this.activityEvents.forEach(event =>
      window.addEventListener(event, () => this.onUserActivity())
    );

    this.session_warning$
        .pipe(takeUntil(this.destroy$))
         .subscribe((show: boolean) => {
            this.show_warn_modal = show;
    });
  }

  /** ✅ Call AFTER login **/
  start() {
    console.log('Session Idle Watcher Started');
    if (this.active) return;

    this.active = true;
    this.session_expired= false;
    this.reset$.subscribe(() => {
      this.clearAllTimers();
      this.session_warning$.next(false);
      this.startTimers();
    });

    this.reset$.next(); // first run
  }

  /** ✅ Call on logout **/
  stop() {
    console.log('Session Idle Watcher Stopped');
    this.active = false;
    this.clearAllTimers();
    this.session_warning$.next(false);
  }

  private onUserActivity() {
    if (!this.active || this.show_warn_modal) return;
    //console.log('User activity detected, resetting session timers.');
    this.broadcast.postMessage({ type: 'reset' });
    this.reset$.next();
  }

  private handleBroadcast(event: MessageEvent) {
    if (!this.active) return;

    const msg = event.data;
    this.ngZone.run(() => {
          if (msg.type === 'reset') {
      this.reset$.next();
    } else if (msg.type === 'timeout') {
      this.forceLogout();
    } else if (msg.type === 'showWarning') {
      this.session_warning$.next(true);
    }
    });

  }

  private startTimers() {

    const idle_mins = this.idle_ttl_minutes * 60 * 1000;
    this.warningTimeoutSub = timer(idle_mins - this.warningTime).subscribe(() => {
      this.broadcast.postMessage({ type: 'showWarning' });
      this.session_warning$.next(true);
      this.startCountdown();
    });

    this.sessionTimeoutSub = timer(idle_mins).subscribe(() => {
      this.broadcast.postMessage({ type: 'timeout' });
      this.forceLogout();
    });
  }

  private startCountdown() {
    this.count_down$.next(this.warningCountdown);
    this.countdownSub = interval(1000).subscribe(() => {
      const current = this.count_down$.value;
      if (current > 1) {
        this.session_warning$.next(true);
        this.count_down$.next(current - 1);
      } else {
        this.countdownSub?.unsubscribe();
      }
    });
  }



  public extend_session(){
    this.broadcast.postMessage({ type: 'reset' });
    this.reset$.next();
  }

  private forceLogout() {
    this.stop();
    this.session_expired=true;
    this.session_logout_subject.next();
  }

  private clearAllTimers() {
    this.warningTimeoutSub?.unsubscribe();
    this.sessionTimeoutSub?.unsubscribe();
    this.countdownSub?.unsubscribe();
  }

  ngOnDestroy() {
    this.broadcast.close();
    this.clearAllTimers();
    this.activityEvents.forEach(event =>
      window.removeEventListener(event, () => this.onUserActivity())
    );
  }
}
